import { useEffect, useMemo, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCopy,
  faCheck,
  faShareNodes,
  faMobileButton,
  faPaperPlane,
} from '@fortawesome/pro-solid-svg-icons';
import { Label } from '@playcastdotio/Shadcn';
import clsx from 'clsx';
import { beaconCoordinator } from '../../../coordinators/init';
import { BeaconOutboundManager } from '../managers/BeaconOutboundManager';
import { BeaconInviteModule } from './BeaconInviteModule';

type BeaconShareMenuProps = {
  className?: string;
  position?: 'top' | 'bottom';
};

function copyToClipboard(text: string): void {
  if (window.playcast?.copyToClipboard) {
    window.playcast.copyToClipboard(text);
  } else {
    navigator.clipboard.writeText(text).catch((err) => {
      console.warn('[BeaconShareMenu] Clipboard write failed:', err);
    });
  }
}

export const BeaconShareMenu = ({ className, position = 'bottom' }: BeaconShareMenuProps) => {
  const [shareLink, setShareLink] = useState<string | null>(
    () => beaconCoordinator?.getShareLink() ?? null,
  );
  const [show, setShow] = useState(false);
  const [copied, setCopied] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const outboundManager = useMemo(() => {
    if (!beaconCoordinator) return null;
    return new BeaconOutboundManager(beaconCoordinator);
  }, []);

  useEffect(() => {
    if (!beaconCoordinator) return;
    const store = beaconCoordinator.getBeaconStore();
    const listenerId = store.addListener((state) => {
      setShareLink(state.shareLink);
    });
    return () => store.removeListener(listenerId);
  }, []);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      outboundManager?.cleanup();
    };
  }, [outboundManager]);

  const handleCopyClicked = () => {
    if (!shareLink) return;
    copyToClipboard(shareLink);
    setCopied(true);
    setShow(false);
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => setCopied(false), 2000);
  };

  const isDisabled = !shareLink;

  return (
    <div className={clsx('relative flex w-full flex-col gap-2', className)} data-testid='beacon-share-menu'>
      <div
        className={clsx(
          isDisabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer',
          'flex h-12 w-full items-center justify-center rounded-lg border-2 border-amber-200 bg-inherit p-2 text-amber-200',
        )}
        onMouseDown={() => {
          if (isDisabled) return;
          setShow((prev) => !prev);
          setTimeout(() => dropdownRef.current?.focus(), 0);
        }}
      >
        <div className='flex flex-1 items-center justify-center text-center'>
          <FontAwesomeIcon icon={copied ? faCheck : faShareNodes} className='!text-amber-200' />
          <span className={clsx(isDisabled ? 'cursor-not-allowed' : 'cursor-pointer', 'pl-2 font-semibold')}>
            {copied ? 'Copied!' : 'Share'}
          </span>
        </div>
      </div>

      <div
        ref={dropdownRef}
        tabIndex={0}
        onBlur={() => setShow(false)}
        className={clsx(
          position === 'bottom' ? 'top-12' : 'bottom-12',
          'absolute z-10 my-2 flex min-w-[180px] flex-col gap-2 rounded-md bg-gray-300 p-2 text-gray-900 shadow',
          show ? 'block' : 'hidden',
        )}
      >
        <button className='group flex items-center' onMouseDown={handleCopyClicked} data-testid='beacon-copy-link-button'>
          <div className='group-hover:border-midnight-bright flex w-full cursor-pointer gap-2 rounded-md border-2 border-gray-300 p-2 text-left text-sm font-normal group-hover:border-dashed'>
            <FontAwesomeIcon icon={faCopy} />
            <Label className='cursor-pointer'>Copy Link</Label>
          </div>
        </button>
        {outboundManager && (
          <>
            <BeaconInviteModule
              title='SMS Invite'
              buttonText='SMS'
              buttonIcon={faMobileButton}
              placeholder='Enter Phone Number'
              type='sms'
              outboundManager={outboundManager}
              disabled={isDisabled}
            />
            <BeaconInviteModule
              title='Email Invite'
              buttonText='Email'
              buttonIcon={faPaperPlane}
              placeholder='email@domain.com'
              type='email'
              outboundManager={outboundManager}
              disabled={isDisabled}
            />
          </>
        )}
      </div>
    </div>
  );
};
